import React, { useState, useEffect, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import apiClient from '../api/client';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { alpha } from '@mui/material/styles';

export default function SkuInfo({ domain, skuCode }) {
    const temp = {
        "inactive_date": "2022-03-28 06:12:57.0",
        "stock_status": "TBA",
        "product_image": "http://www.stevenshobby.com/si/ProductFiles/DML7203.jpg",
        "creation_date": "2022-03-28 06:12:52.0",
        "product_code": "DML7203",
        "in_stock": 0,
        "retail_price": 34.95,
        "product_scale": "1/72",
        "inactive_product": 0,
        "domain_id": 10,
        "product_maker": "DML/DRAGON MODELS",
        "check_date": "2022-04-01 00:00:00.0",
        "price": 20.97,
        "domain": "stevenshobby.com",
        "product_description": "SdKfz 181 Ausf E Tiger I Late Prod Tank w/Zimmerit (Re-Issue)",
        "product_url": ""
    };
    
    const [sku, setSku] = useState(temp);
    const [snackOpen, setSnackState] = useState(false);
    const [snackMsg, setSnackMsg] = useState('');
    const [snackSeverity, setSnackSeverity] = useState('success');
    const [isRefreshing, setIsRefreshing] = useState(false);

    const getSKUDetails = useCallback(async () => {
        try {
            const url = '/sku?sku=' + skuCode + "&domain=" + encodeURIComponent(domain);
            const res = await apiClient.get(url);
            if (res.data && res.data.length > 0) {
                setSku(res.data[0]);
            }
        } catch (error) {
            console.error('Error fetching SKU details:', error);
        }
    }, [domain, skuCode]);

    const refreshSKU = async () => {
        setIsRefreshing(true);
        try {
            const url = '/sku-refresh?url=' + encodeURIComponent(sku.product_url);
            await apiClient.get(url);
            setSnackMsg("Item refreshed successfully");
            setSnackSeverity('success');
            setSnackState(true);
            getSKUDetails();
        } catch (error) {
            console.error('Error refreshing SKU:', error);
            setSnackMsg("Failed to refresh SKU");
            setSnackSeverity('error');
            setSnackState(true);
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleSnackClose = () => {
        setSnackState(false);
    };

    useEffect(() => {
        getSKUDetails();
    }, [getSKUDetails]);

    const inStock = sku.in_stock === true || sku.in_stock === 1;

    const attributes = [
        { label: 'Vendor', value: sku.domain },
        { label: 'SKU Code', value: sku.product_code },
        { label: 'Manufacturer', value: sku.product_maker },
        { label: 'Price', value: sku.price != null ? `$${sku.price}` : '—' },
        { 
            label: 'Stock Status', 
            badge: inStock ? (
                <Chip label="In Stock" size="small" color="success" />
            ) : (
                <Chip label="Out of Stock" size="small" color="error" />
            )
        },
        { label: 'Scale', value: sku.product_scale || 'N/A' },
        { label: 'Last Checked', value: sku.check_date || '—' },
        { label: 'Date Added', value: sku.creation_date || '—' },
        { 
            label: 'Catalog State', 
            badge: (
                <Chip
                    label={sku.inactive_product === 0 ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                        bgcolor: sku.inactive_product === 0 ? (t) => alpha(t.palette.primary.main, 0.15) : 'action.hover',
                        color: sku.inactive_product === 0 ? 'primary.light' : 'text.secondary',
                        border: 1,
                        borderColor: 'divider',
                    }}
                />
            )
        },
    ];

    return (
        <Paper sx={{ p: 3.5, width: '100%' }}>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackOpen}
                onClose={handleSnackClose}
                autoHideDuration={4000}
            >
                <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%', borderRadius: 2 }}>
                    {snackMsg}
                </Alert>
            </Snackbar>

            <Grid container spacing={4} alignItems="center">
                {sku.product_image && (
                    <Grid xs={12} md={4} display="flex" justifyContent="center">
                        <Box
                            sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 1.5,
                                bgcolor: 'background.default',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                maxWidth: 360,
                                maxHeight: 360,
                            }}
                        >
                            <img
                                src={sku.product_image}
                                style={{ maxHeight: '280px', maxWidth: '100%', objectFit: 'contain' }}
                                alt={sku.product_description}
                            />
                        </Box>
                    </Grid>
                )}

                <Grid xs={12} md={sku.product_image ? 8 : 12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', flexGrow: 1 }}>
                            {sku.product_description}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {sku.product_url && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<OpenInNewIcon />}
                                    href={sku.product_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Vendor URL
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={refreshSKU}
                                disabled={isRefreshing}
                            >
                                {isRefreshing ? 'Refreshing...' : 'Refresh'}
                            </Button>
                        </Box>
                    </Box>

                    <Grid container spacing={2.5} sx={{ mt: 1 }}>
                        {attributes.map((attr, idx) => (
                            <Grid xs={6} sm={4} key={idx}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600, mb: 0.5 }}>
                                    {attr.label}
                                </Typography>
                                {attr.badge ? attr.badge : (
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                        {attr.value}
                                    </Typography>
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
}
